import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import type { CategoryEntry, DocEntry } from '../api/types';

// Re-export types for backward compatibility
export type { CategoryEntry, DocEntry };

/**
 * Fetch all categories from the API.
 */
export function useCategories() {
  const [categories, setCategories] = useState<CategoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.getCategories()
      .then((data) => {
        setCategories(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  return { categories, loading, error };
}

/**
 * Fetch docs, optionally filtered by category.
 */
export function useDocs(category?: string, status?: string) {
  const [docs, setDocs] = useState<DocEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.listDocs({ category, status })
      .then((data) => {
        setDocs(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [category, status]);

  return { docs, loading, error };
}

/**
 * Fetch a single document's markdown content by slug.
 */
export function useDocContent(slug: string) {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    api.getDoc(slug)
      .then(({ content: c }) => {
        setContent(c);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => { refetch(); }, [refetch]);

  return { content, loading, error, refetch };
}
